import React, { useCallback, useState } from 'react';
import { ErrorApi, AlertApi } from '@backstage/core-plugin-api';
import { ExecuteActionAllOptions } from '../api';
import { MenuPosition } from './types';
import {
  ProjectCodeActionType,
  ProjectCodeResourceType,
  ExecuteActionResponse,
  CRDAWSConfig,
  CRDGCPConfig,
  CRDAzureConfig,
  CRDKubernetesConfig,
  GCPActionDetails,
  AWSActionDetails,
  AzureActionDetails,
  KubernetesActionDetails,
} from '@internal/backstage-plugin-compute-resource-discovery-new-common';

interface UseActionManagementProps {
  executeAction: (
    options: ExecuteActionAllOptions,
  ) => Promise<ExecuteActionResponse>;
  refresh: () => void;
  gcpConfigs: CRDGCPConfig[];
  awsConfigs: CRDAWSConfig[];
  azureConfigs: CRDAzureConfig[];
  kubernetesConfigs: CRDKubernetesConfig[];
  errorApi: ErrorApi;
  alertApi: AlertApi;
  entityRef: string;
  projectCode: string;
}

export const useActionManagement = ({
  executeAction,
  refresh,
  errorApi,
  alertApi,
  gcpConfigs,
  awsConfigs,
  azureConfigs,
  kubernetesConfigs,
  entityRef,
  projectCode,
}: UseActionManagementProps) => {
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({
    mouseX: null,
    mouseY: null,
  });
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [rdsConnectionDialogOpen, setRdsConnectionDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] =
    useState<ProjectCodeActionType | null>(null);
  const [service, setService] = useState<ProjectCodeResourceType | null>(null);
  const [isActionInProgress, setActionInProgress] = useState<boolean>(false);

  // Handle opening the action menu from table-level action
  const handleTableActionClick = useCallback((event: React.MouseEvent) => {
    setMenuPosition({
      mouseX: event.clientX,
      mouseY: event.clientY,
    });
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuPosition({
      mouseX: null,
      mouseY: null,
    });
  }, []);

  const handleActionClick = useCallback(
    (action: ProjectCodeActionType, service: ProjectCodeResourceType) => {
      setSelectedAction(action);
      setService(service);
      setConfirmationDialogOpen(true);
      handleMenuClose();
    },
    [handleMenuClose],
  );

  const handleOpenConnection = useCallback(() => {
    setRdsConnectionDialogOpen(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleConfirmAction = useCallback(async () => {
    if (!selectedAction || !service) return;

    setConfirmationDialogOpen(false);
    setActionInProgress(true);

    let gcpActions: GCPActionDetails[] = [];
    let azureActions: AzureActionDetails[] = [];
    let awsActions: AWSActionDetails[] = [];
    let kubernetesActions: KubernetesActionDetails[] = [];
    for (const gcpConfig of gcpConfigs) {
      if (gcpConfig.resourceTypes) {
        if (gcpConfig.resourceTypes.includes('vm')) {
          gcpActions.push({
            action: selectedAction,
            resourceType: 'vm',
            projectId: gcpConfig.projectId,
            projectCode,
          });
        }
      }
    }
    for (const azureConfig of azureConfigs) {
      if (azureConfig && azureConfig.resourceTypes) {
        if (azureConfig.resourceTypes.includes('vm')) {
          azureActions.push({
            action: selectedAction,
            resourceType: 'vm',
            resourceGroup: azureConfig.resourceGroup,
            projectCode,
          });
        }
        if (azureConfig.resourceTypes.includes('vmss')) {
          azureActions.push({
            action: selectedAction,
            resourceType: 'vmss',
            resourceGroup: azureConfig.resourceGroup,
            projectCode,
          });
        }
      }
    }

    for (const awsConfig of awsConfigs) {
      if (awsConfig && awsConfig.resourceTypes) {
        if (awsConfig.resourceTypes.includes('ec2')) {
          awsActions.push({
            action: selectedAction,
            resourceType: 'ec2',
            accountId: awsConfig.accountId,
            region: awsConfig.region,
            projectCode,
          });
        }
        if (awsConfig.resourceTypes.includes('rds')) {
          awsActions.push({
            action: selectedAction,
            resourceType: 'rds',
            accountId: awsConfig.accountId,
            region: awsConfig.region,
            projectCode,
          });
        }
        if (awsConfig.resourceTypes.includes('asg')) {
          awsActions.push({
            action: selectedAction,
            resourceType: 'asg',
            accountId: awsConfig.accountId,
            region: awsConfig.region,
            projectCode,
          });
        }
      }
    }

    for (const kubernetesConfig of kubernetesConfigs) {
      if (kubernetesConfig) {
        kubernetesActions.push({
          action: selectedAction,
          resourceType: 'resources',
          kubeContext: kubernetesConfig.kubeContext,
          projectCode,
        });
      }
    }

    try {
      const response = await executeAction({
        entityRef,
        gcp: gcpActions,
        azure: azureActions,
        aws: awsActions,
        kubernetes: kubernetesActions,
      });

      if (response.status === 'success') {
        alertApi.post({
          message: `Action ${selectedAction} for ${service} has been initiated. ${response.response?.message}`,
          severity: 'success',
        });
        refresh();
      } else {
        throw new Error(response.error || 'Unknown error');
      }
    } catch (error) {
      errorApi.post(
        new Error(`Error to execute action ${selectedAction} for ${service}`),
      );
    } finally {
      setActionInProgress(false);
      setSelectedAction(null);
      setService(null);
    }
  }, [
    selectedAction,
    executeAction,
    refresh,
    gcpConfigs,
    awsConfigs,
    kubernetesConfigs,
    azureConfigs,
    projectCode,
    entityRef,
  ]);

  return {
    menuPosition,
    confirmationDialogOpen,
    rdsConnectionDialogOpen,
    selectedAction,
    isActionInProgress,
    handleTableActionClick,
    handleMenuClose,
    handleActionClick,
    handleConfirmAction,
    handleOpenConnection,
    setConfirmationDialogOpen,
    setRdsConnectionDialogOpen,
  };
};
